import { Heart, X } from '@/core/ui/icons';
import { useState } from 'react';
import { useTrackView } from '@/core/tracking';
import { Button, cn, Skeleton } from '@/core/ui';
import { useProduct } from '../application/queries';
import { DETAIL_TABS, hasContent, type DetailTab, type Product } from '../domain/product';
import { ProductImage } from './ProductImage';

interface Props {
  slug: string;
  isFavorite: (productId: string) => boolean;
  onToggleFavorite: (productId: string) => void;
  onClose: () => void;
}

/**
 * Detalhe do produto ao lado da vitrine. No desktop é uma coluna fixa à
 * direita; no celular cobre a tela, com o fundo escurecido.
 */
export function ProductDetailPanel({ slug, isFavorite, onToggleFavorite, onClose }: Props) {
  const product = useProduct(slug);

  return (
    <>
      <div aria-hidden className="fixed inset-0 z-30 bg-black/60 xl:hidden" onClick={onClose} />
      <aside
        aria-label="Detalhe do produto"
        className={cn(
          'glass-strong shadow-float fixed inset-y-0 right-0 z-40 w-full max-w-sm overflow-y-auto px-5 py-5',
          'xl:sticky xl:top-16 xl:z-auto xl:h-[calc(100vh-4rem)] xl:max-w-none xl:w-96 xl:shrink-0 xl:bg-surface/60 xl:shadow-none xl:backdrop-blur-xl',
        )}
      >
        {product.isPending ? (
          <div className="space-y-4">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="aspect-[4/3] rounded-lg" />
            <Skeleton className="h-10" />
          </div>
        ) : !product.data ? (
          <div className="py-10 text-center text-sm text-fg-subtle">
            Produto não encontrado.
            <div className="mt-4">
              <Button variant="secondary" size="sm" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        ) : (
          // `key`: trocar de produto volta para a primeira aba.
          <Detail
            key={product.data.id}
            product={product.data}
            favorite={isFavorite(product.data.id)}
            onToggleFavorite={onToggleFavorite}
            onClose={onClose}
          />
        )}
      </aside>
    </>
  );
}

function Detail({
  product,
  favorite,
  onToggleFavorite,
  onClose,
}: {
  product: Product;
  favorite: boolean;
  onToggleFavorite: (productId: string) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>('usage');
  useTrackView({
    action: 'product.viewed',
    targetType: 'product',
    targetId: product.id,
    targetLabel: product.name,
  });

  return (
    <div>
      <div className="flex items-start gap-2">
        <h2 className="flex-1 text-sm leading-snug font-semibold">{product.name}</h2>
        <button
          type="button"
          onClick={() => onToggleFavorite(product.id)}
          aria-pressed={favorite}
          aria-label={favorite ? 'Remover dos favoritos' : 'Favoritar'}
          className="rounded-lg p-1.5 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
        >
          <Heart
            aria-hidden
            weight={favorite ? 'fill' : 'duotone'}
            className={cn('size-4', favorite && 'text-brand-400')}
          />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar detalhe"
          className="rounded-lg p-1.5 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>

      <div className="mt-3 flex aspect-[4/3] items-center justify-center rounded-3xl bg-white p-4 light:bg-surface-2">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="h-full w-auto object-contain light:mix-blend-multiply"
          />
        ) : (
          <ProductImage packaging={product.packaging} brand={product.brand} />
        )}
      </div>

      {/* Sem contexto de pedidos no back, ainda não há para onde levar. */}
      <Button size="lg" className="mt-4 w-full" disabled title="Página do produto em breve">
        Ver produto
      </Button>

      <div role="tablist" className="mt-6 grid grid-cols-3 gap-1 rounded-2xl bg-surface-2 p-1">
        {DETAIL_TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'rounded-xl px-2 py-2 text-xs font-medium transition-colors',
              tab === t.id ? 'bg-surface-3 text-fg' : 'text-fg-subtle hover:bg-surface-3/60 hover:text-fg',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" className="mt-5 space-y-5 text-sm leading-relaxed text-fg-muted">
        {!hasContent(product.details, tab) ? (
          <p className="text-fg-subtle">
            {tab === 'description' ? product.summary : 'Informação ainda não cadastrada.'}
          </p>
        ) : tab === 'composition' ? (
          <ul className="space-y-0.5">
            {product.details!.composition.map((c) => (
              <li
                key={c.ingredient}
                className="flex justify-between gap-4 rounded-xl px-3 py-2 odd:bg-surface-2/60"
              >
                <span>{c.ingredient}</span>
                {c.amount && <span className="text-fg">{c.amount}</span>}
              </li>
            ))}
          </ul>
        ) : (
          product.details![tab].map((block, i) => (
            <section key={i} className="space-y-3">
              {block.title && (
                <h3 className="text-xs font-semibold tracking-wide text-fg uppercase">{block.title}</h3>
              )}
              {block.paragraphs.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
}
